// Technical Staff View Modal

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
   Tooltip,
   TooltipContent,
   TooltipProvider,
   TooltipTrigger,
} from "@/components/ui/tooltip";
import { gql, useQuery } from '@apollo/client';
import { ChevronDown, Download, Eye, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import COVDetailsRenderer from './ApplicationDetailsRenderers/COVDetailsRenderer';
import CSAWDetailsRenderer from './ApplicationDetailsRenderers/CSAWDetailsRenderer';
import PLTCPDetailsRenderer from './ApplicationDetailsRenderers/PLTCPDetailsRenderer';
import PLTPDetailsRenderer from './ApplicationDetailsRenderers/PLTPDetailsRenderer';
import PTPRDetailsRenderer from './ApplicationDetailsRenderers/PTPRDetailsRenderer';
import TCEBPDetailsRenderer from './ApplicationDetailsRenderers/TCEBPDetailsRenderer';

const GET_CSAW_PERMIT = gql`
   query GetCSAWPermit($id: ID!) {
      getCSAWPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         status
         currentStage
         registrationType
         chainsawStore
         ownerName
         address
         phone
         purpose
         brand
         model
         serialNumber
         dateOfAcquisition
         powerOutput
         maxLengthGuidebar
         countryOfOrigin
         purchasePrice
         isOwner
         isTenureHolder
         isBusinessOwner
         isPTPRHolder
         isWPPHolder
         files {
         officialReceipt {
            filename
            contentType
            data
         }
         deedOfSale {
            filename
            contentType
            data
         }
         specialPowerOfAttorney {
            filename
            contentType
            data
         }
         forestTenureAgreement {
            filename
            contentType
            data
         }
         businessPermit {
            filename
            contentType
            data
         }
         certificateOfRegistration {
            filename
            contentType
            data
         }
         woodProcessingPlantPermit {
            filename
            contentType
            data
         }
         }
      }
   }
   `;

const GET_COV_PERMIT = gql`
   query GetCOVPermit($id: ID!) {
      getCOVPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         status
         currentStage
         name
         address
         cellphone
         purpose
         driverName
         driverAddress
         driverLicenseNumber
         vehiclePlateNumber
         originAddress
         destinationAddress
         files {
         letterOfIntent {
            filename
            contentType
            data
         }
         tallySheet {
            filename
            contentType
            data
         }
         forestCertification {
            filename
            contentType
            data
         }
         orCr {
            filename
            contentType
            data
         }
         driverLicense {
            filename
            contentType
            data
         }
         specialPowerOfAttorney {
            filename
            contentType
            data
         }
         }
      }
   }
   `;

const GET_PTPR_PERMIT = gql`
   query GetPTPRPermit($id: ID!) {
      getPTPRPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         status
         currentStage
         ownerName
         address
         contactNumber
         lotArea
         treeSpecies
         totalTrees
         treeSpacing
         yearPlanted
         isCLOA
         isRepresentative
         files {
         letterRequest {
            filename
            contentType
            data
         }
         titleOrTaxDeclaration {
            filename
            contentType
            data
         }
         darCertification {
            filename
            contentType
            data
         }
         specialPowerOfAttorney {
            filename
            contentType
            data
         }
         }
      }
   }
   `;

const GET_PLTCP_PERMIT = gql`
   query GetPLTCPPermit($id: ID!) {
      getPLTCPPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         requestType
         status
         currentStage
         name
         address
         contactNumber
         treeType
         treeStatus
         landType
         posingDanger
         forPersonalUse
         purpose
         isWithinSubdivision
         isSchoolOrOrganization
         occupation
         projectLocation
         projectAddress
         files {
         letterOfIntent {
            filename
            contentType
            data
         }
         applicationLetter {
            filename
            contentType
            data
         }
         lguEndorsement {
            filename
            contentType
            data
         }
         homeownersResolution {
            filename
            contentType
            data
         }
         ptaResolution {
            filename
            contentType
            data
         }
         }
      }
   }
   `;

const GET_PLTP_PERMIT = gql`
   query GetPLTPPermit($id: ID!) {
      getPLTPPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         requestType
         status
         currentStage
         name
         address
         contactNumber
         treeType
         treeStatus
         landType
         posingDanger
         forPersonalUse
         purpose
         isCLOA
         isRepresentative
         isSchoolOrOrganization
         files {
         letterOfIntent {
            filename
            contentType
            data
         }
         applicationLetter {
            filename
            contentType
            data
         }
         lguEndorsement {
            filename
            contentType
            data
         }
         titleCertificate {
            filename
            contentType
            data
         }
         darCertificate {
            filename
            contentType
            data
         }
         specialPowerOfAttorney {
            filename
            contentType
            data
         }
         ptaResolution {
            filename
            contentType
            data
         }
         }
      }
   }
   `;

const GET_TCEBP_PERMIT = gql`
   query GetTCEBPPermit($id: ID!) {
      getTCEBPPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         status
         currentStage
         name
         projectLocation
         officeName
         officeAddress
         contactNumber
         requestType
         purpose
         isWithinTimberland
         isTitledProperty
         isWithinProtectedArea
         isRequestForCutting
         isRequestForEarthBalling
         files {
         letterOfIntent {
            filename
            contentType
            data
         }
         applicationLetter {
            filename
            contentType
            data
         }
         lguEndorsement {
            filename
            contentType
            data
         }
         landTenurial {
            filename
            contentType
            data
         }
         siteDevelopmentPlan {
            filename
            contentType
            data
         }
         environmentalCompliance {
            filename
            contentType
            data
         }
         fpic {
            filename
            contentType
            data
         }
         ownerConsent {
            filename
            contentType
            data
         }
         pambClearance {
            filename
            contentType
            data
         }
         }
      }
   }
   `;

const ApplicationViewModal = ({ isOpen, onClose, application }) => {
   const [permitData, setPermitData] = useState(null);
   const [showScrollIndicator, setShowScrollIndicator] = useState(false);
   const scrollAreaRef = useRef(null);

   // Query for CSAW permit data
   const { loading: csawLoading, error: csawError, data: csawData } = useQuery(GET_CSAW_PERMIT, {
      variables: { id: application?.id },
      skip: !application?.id || application?.applicationType !== 'Chainsaw Registration',
   });

   // Query for COV permit data
   const { loading: covLoading, error: covError, data: covData } = useQuery(GET_COV_PERMIT, {
      variables: { id: application?.id },
      skip: !application?.id || application?.applicationType !== 'Certificate of Verification',
   });

   // Query for PTPR permit data
   const { loading: ptprLoading, error: ptprError, data: ptprData } = useQuery(GET_PTPR_PERMIT, {
      variables: { id: application?.id },
      skip: !application?.id || application?.applicationType !== 'Private Tree Plantation Registration',
   });

   // Query for PLTCP permit data
   const { loading: pltcpLoading, error: pltcpError, data: pltcpData } = useQuery(GET_PLTCP_PERMIT, {
      variables: { id: application?.id },
      skip: !application?.id || application?.applicationType !== 'Public Land Tree Cutting Permit',
   });

   // Query for PLTP permit data
   const { loading: pltpLoading, error: pltpError, data: pltpData } = useQuery(GET_PLTP_PERMIT, {
      variables: { id: application?.id },
      skip: !application?.id || application?.applicationType !== 'Private Land Timber Permit',
   });

   // Query for TCEBP permit data
   const { loading: tcebpLoading, error: tcebpError, data: tcebpData } = useQuery(GET_TCEBP_PERMIT, {
      variables: { id: application?.id },
      skip: !application?.id || application?.applicationType !== 'Tree Cutting and/or Earth Balling Permit',
   });

   useEffect(() => {
      if (csawData?.getCSAWPermitById) {
         setPermitData(csawData.getCSAWPermitById);
      } else if (covData?.getCOVPermitById) {
         setPermitData(covData.getCOVPermitById);
      } else if (ptprData?.getPTPRPermitById) {
         setPermitData(ptprData.getPTPRPermitById);
      } else if (pltcpData?.getPLTCPPermitById) {
         // console.log('PLTCP Data received:', pltcpData.getPLTCPPermitById);
         // Transform boolean values explicitly
         const transformedData = {
            ...pltcpData.getPLTCPPermitById,
            posingDanger: Boolean(pltcpData.getPLTCPPermitById.posingDanger),
            forPersonalUse: Boolean(pltcpData.getPLTCPPermitById.forPersonalUse),
            isWithinSubdivision: Boolean(pltcpData.getPLTCPPermitById.isWithinSubdivision),
            isSchoolOrOrganization: Boolean(pltcpData.getPLTCPPermitById.isSchoolOrOrganization)
         };
         // console.log('Transformed PLTCP Data:', transformedData);
         setPermitData(transformedData);
      } else if (pltpData?.getPLTPPermitById) {
         const transformedData = {
            ...pltpData.getPLTPPermitById,
            // Only transform boolean values if they exist
            posingDanger: pltpData.getPLTPPermitById.posingDanger !== null ?
               Boolean(pltpData.getPLTPPermitById.posingDanger) : null,
            forPersonalUse: pltpData.getPLTPPermitById.forPersonalUse !== null ?
               Boolean(pltpData.getPLTPPermitById.forPersonalUse) : null,
            isCLOA: Boolean(pltpData.getPLTPPermitById.isCLOA),
            isRepresentative: Boolean(pltpData.getPLTPPermitById.isRepresentative),
            isSchoolOrOrganization: Boolean(pltpData.getPLTPPermitById.isSchoolOrOrganization)
         };
         setPermitData(transformedData);
      } else if (tcebpData?.getTCEBPPermitById) {
         // console.log('TCEBP Data received:', tcebpData.getTCEBPPermitById);
         // Transform boolean values explicitly
         const transformedData = {
            ...tcebpData.getTCEBPPermitById,
            isWithinTimberland: Boolean(tcebpData.getTCEBPPermitById.isWithinTimberland),
            isTitledProperty: Boolean(tcebpData.getTCEBPPermitById.isTitledProperty),
            isWithinProtectedArea: Boolean(tcebpData.getTCEBPPermitById.isWithinProtectedArea),
            isRequestForCutting: Boolean(tcebpData.getTCEBPPermitById.isRequestForCutting),
            isRequestForEarthBalling: Boolean(tcebpData.getTCEBPPermitById.isRequestForEarthBalling)
         };
         // console.log('Transformed TCEBP Data:', transformedData);
         setPermitData(transformedData);
      }
   }, [csawData, covData, ptprData, pltcpData, pltpData, tcebpData]);

   // Show scroll indicator for 3 seconds when modal opens
   useEffect(() => {
      if (isOpen) {
         // Show the indicator
         setShowScrollIndicator(true);

         // Hide it after 3 seconds
         const timer = setTimeout(() => {
            setShowScrollIndicator(false);
         }, 3000);

         return () => clearTimeout(timer);
      }
   }, [isOpen, permitData]);

   if (!application) return null;

   const handleDownload = (file) => {
      const byteCharacters = atob(file.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
         byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: file.contentType });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
   };

   const handlePreview = (file) => {
      try {
         const byteCharacters = atob(file.data);
         const byteNumbers = new Array(byteCharacters.length);
         for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
         }
         const byteArray = new Uint8Array(byteNumbers);
         const blob = new Blob([byteArray], { type: file.contentType });
         const fileUrl = URL.createObjectURL(blob);

         // For images
         if (file.contentType.startsWith('image/')) {
            const newWindow = window.open('', '_blank');
            if (newWindow) {
               newWindow.document.write(`
                     <html>
                        <head>
                           <title>${file.filename}</title>
                           <style>
                              body {
                                 margin: 0;
                                 display: flex;
                                 justify-content: center;
                                 align-items: center;
                                 min-height: 100vh;
                                 background: #f1f5f9;
                              }
                              img {
                                 max-width: 100%;
                                 max-height: 100vh;
                                 object-fit: contain;
                              }
                           </style>
                        </head>
                        <body>
                           <img src="${fileUrl}" alt="${file.filename}" />
                        </body>
                     </html>
                  `);
               newWindow.document.close();

               // Clean up URL after a delay
               setTimeout(() => {
                  URL.revokeObjectURL(fileUrl);
               }, 1000);
            } else {
               toast.error('Pop-up was blocked. Please allow pop-ups for this site.');
               handleDownload(file);
            }
         }
         // For PDFs
         else if (file.contentType === 'application/pdf') {
            // Create an iframe to display the PDF
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            const newWindow = window.open('', '_blank');
            if (newWindow) {
               newWindow.location = fileUrl;

               // Clean up
               setTimeout(() => {
                  URL.revokeObjectURL(fileUrl);
                  document.body.removeChild(iframe);
               }, 1000);
            } else {
               toast.error('Pop-up was blocked. Please allow pop-ups for this site.');
               handleDownload(file);
               URL.revokeObjectURL(fileUrl);
               document.body.removeChild(iframe);
            }
         }
         // For other file types
         else {
            toast.error('Preview not supported for this file type. Downloading instead...');
            handleDownload(file);
            URL.revokeObjectURL(fileUrl);
         }
      } catch (error) {
         console.error('Preview error:', error);
         toast.error('Failed to preview file. Downloading instead...');
         handleDownload(file);
      }
   };


   const renderStatusInfo = () => {
      const getHandlingPersonnel = () => {
         const formatRole = (role) => {
            return role?.replace(/_/g, ' ') || 'Staff';
         };

         if (application.currentStage === 'TechnicalStaffReview') {
            const admin = JSON.parse(localStorage.getItem('admin'));
            return admin ? `Technical Staff - ${admin.firstName} ${admin.lastName}` : 'Technical Staff';

         } else if (application.OOPCreated === true && application.awaitingOOP === false) {
            const admin = JSON.parse(localStorage.getItem('admin'));
            // Format the role by replacing underscores with spaces
            const role = formatRole(admin?.roles?.[0]); // Access first role from the roles array
            return admin ? `${role} - ${admin.firstName} ${admin.lastName}` : 'Technical Staff';

         } else if (application.assignedTechnicalStaffName) {
            const admin = JSON.parse(localStorage.getItem('admin'));
            const role = formatRole(admin?.roles?.[0]); // Access first role from the roles array
            return admin ? `${role} - ${application.assignedTechnicalStaffName}` : 'Technical Staff';
         }
         return null;
      };

      const handlingPersonnel = getHandlingPersonnel();

      return (
         <div className="flex flex-col gap-2 items-end">
            <Badge variant="outline" className={
               application.status === 'Approved' ? 'bg-green-100 text-green-800' :
                  application.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                     'bg-blue-100 text-blue-800'
            }>
               {application.status}
            </Badge>
            <Badge variant="secondary">
               {application.currentStage}
            </Badge>
            {handlingPersonnel && (
               <div className="text-sm text-muted-foreground">
                  <span className="font-medium">Currently handled by:</span>{' '}
                  <span className="text-blue-600">{handlingPersonnel}</span>
               </div>
            )}
         </div>
      );
   };

   // For the loading state
   if (csawLoading || covLoading || ptprLoading || pltcpLoading || pltpLoading || tcebpLoading) {
      return (
         <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
               <DialogHeader>
                  <DialogTitle>Loading Application</DialogTitle>
                  <DialogDescription>
                     Loading permit details...
                  </DialogDescription>
               </DialogHeader>
               <div className="flex items-center justify-center p-6">
                  <Loader2 className="h-6 w-6 animate-spin" />
               </div>
            </DialogContent>
         </Dialog>
      );
   }

   // For the error state
   if (csawError || covError || ptprError || pltcpError || pltpError || tcebpError) {
      return (
         <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
               <DialogHeader>
                  <DialogTitle>Error</DialogTitle>
                  <DialogDescription>
                     An error occurred while loading the permit details
                  </DialogDescription>
               </DialogHeader>
               <div className="text-red-500 p-6">
                  {csawError?.message || covError?.message || ptprError?.message || pltcpError?.message || pltpError?.message || tcebpError?.message}
               </div>
            </DialogContent>
         </Dialog>
      );
   }

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent
            className="max-w-[95vw] md:max-w-[1200px] h-[90vh] flex flex-col overflow-hidden"
         >
            <DialogHeader className="px-2">
               <DialogTitle>Application Details</DialogTitle>
               <DialogDescription>
                  Application Number: {application.applicationNumber}
               </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col lg:flex-row flex-1 gap-4 lg:gap-6 overflow-hidden h-[calc(100%-8rem)]">
               {/* Left side - Application Details */}
               <div className="flex-1 lg:w-[60%] relative h-[45vh] lg:h-full">
                  <ScrollArea
                     ref={scrollAreaRef}
                     className="h-full pr-4"
                  >
                     <div className="space-y-4 px-2 pb-8">
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                           <div>
                              <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Application Type</h3>
                              <p className="dark:text-gray-200">{application.applicationType}</p>
                           </div>
                           {renderStatusInfo()}
                        </div>

                        <Separator className="dark:bg-gray-700" />

                        {/* Application Type Specific Details */}
                        <div>
                           {application.applicationType === 'Chainsaw Registration' && (
                              <CSAWDetailsRenderer
                                 data={permitData || application}
                                 handlePreview={handlePreview}
                                 handleDownload={handleDownload}
                                 documentsOnRight={true}
                              />
                           )}
                           {application.applicationType === 'Certificate of Verification' && (
                              <COVDetailsRenderer
                                 data={permitData || application}
                                 handlePreview={handlePreview}
                                 handleDownload={handleDownload}
                                 documentsOnRight={true}
                              />
                           )}
                           {application.applicationType === 'Private Tree Plantation Registration' && (
                              <PTPRDetailsRenderer
                                 data={permitData || application}
                                 handlePreview={handlePreview}
                                 handleDownload={handleDownload}
                                 documentsOnRight={true}
                              />
                           )}
                           {application.applicationType === 'Public Land Tree Cutting Permit' && (
                              <PLTCPDetailsRenderer
                                 data={permitData || application}
                                 handlePreview={handlePreview}
                                 handleDownload={handleDownload}
                                 documentsOnRight={true}
                              />
                           )}
                           {application.applicationType === 'Private Land Timber Permit' && (
                              <PLTPDetailsRenderer
                                 data={permitData || application}
                                 handlePreview={handlePreview}
                                 handleDownload={handleDownload}
                                 documentsOnRight={true}
                              />
                           )}
                           {application.applicationType === 'Tree Cutting and/or Earth Balling Permit' && (
                              <TCEBPDetailsRenderer
                                 data={permitData || application}
                                 handlePreview={handlePreview}
                                 handleDownload={handleDownload}
                                 documentsOnRight={true}
                              />
                           )}
                        </div>
                     </div>
                  </ScrollArea>

                  {/* Scroll Indicator with fade out animation */}
                  {showScrollIndicator && (
                     <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 animate-bounce z-10 transition-opacity duration-500">
                        <TooltipProvider delayDuration={200}>
                           <Tooltip>
                              <TooltipTrigger>
                                 <div className="p-2 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors backdrop-blur-sm">
                                    <ChevronDown className="h-5 w-5" />
                                 </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                 <p>Scroll for more details</p>
                              </TooltipContent>
                           </Tooltip>
                        </TooltipProvider>
                     </div>
                  )}
               </div>

               {/* Right side - Documents */}
               <div className="lg:w-[40%] border-t lg:border-t-0 lg:border-l pt-4 lg:pt-0 lg:pl-6 dark:border-gray-700 h-[45vh] lg:h-full">
                  <h3 className="font-semibold mb-4 px-2 dark:text-gray-200">Supporting Documents</h3>
                  <ScrollArea className="h-[calc(100%-3rem)]">
                     <div className="space-y-4 px-2 pb-8">
                        {permitData?.files && Object.entries(permitData.files)
                           .filter(([key]) => key !== '__typename')
                           .map(([key, files]) => {
                              if (!files || (Array.isArray(files) && files.length === 0)) return null;

                              const fileArray = Array.isArray(files) ? files : [files];
                              const displayName = key
                                 .replace(/([A-Z])/g, ' $1')
                                 .replace(/^./, str => str.toUpperCase());

                              return (
                                 <div key={key} className="space-y-2">
                                    <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">{displayName}</h4>
                                    {fileArray.map((file, index) => (
                                       <div
                                          key={index}
                                          className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-2 sm:p-3 rounded-lg"
                                       >
                                          <span className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[150px] sm:max-w-[200px]">
                                             {file.filename}
                                          </span>
                                          <div className="flex gap-1 sm:gap-2">
                                             <TooltipProvider delayDuration={250}>
                                                <Tooltip>
                                                   <TooltipTrigger asChild>
                                                      <Button
                                                         size="icon"
                                                         variant="outline"
                                                         className="h-8 w-8 dark:border-gray-600 dark:hover:bg-gray-700"
                                                         onClick={() => handlePreview(file)}
                                                      >
                                                         <Eye className="h-4 w-4" />
                                                      </Button>
                                                   </TooltipTrigger>
                                                   <TooltipContent>
                                                      <p>Preview</p>
                                                   </TooltipContent>
                                                </Tooltip>
                                             </TooltipProvider>
                                             <TooltipProvider delayDuration={250}>
                                                <Tooltip>
                                                   <TooltipTrigger asChild>
                                                      <Button
                                                         size="icon"
                                                         variant="outline"
                                                         className="h-8 w-8 dark:border-gray-600 dark:hover:bg-gray-700"
                                                         onClick={() => handleDownload(file)}
                                                      >
                                                         <Download className="h-4 w-4" />
                                                      </Button>
                                                   </TooltipTrigger>
                                                   <TooltipContent>
                                                      <p>Download</p>
                                                   </TooltipContent>
                                                </Tooltip>
                                             </TooltipProvider>
                                          </div>
                                       </div>
                                    ))}
                                 </div>
                              );
                           })}
                     </div>
                  </ScrollArea>
               </div>
            </div>

            <DialogFooter className="mt-4 px-2">
               <Button onClick={onClose} className="w-full sm:w-auto dark:hover:bg-gray-700">Close</Button>
            </DialogFooter>
         </DialogContent>
      </Dialog>
   );
};

export default ApplicationViewModal;
